import '@mui/lab/themeAugmentation'
import { createTheme } from '@mui/material'

export const borderRadiusDefault = '20px'
// Inset radius for inner stat boxes / list rows; see _variables.scss $inset-border-radius
export const borderRadiusInset = '8px'
export const lightColorHex = '#777'

// Color constants matching _variables.scss
export const colors = {
  success: '#38a73e',
  warning: '#b13d37',
  subtle: '#bbb',
  lightGrayBackground: '#f2f2f2',
  divider: 'rgba(0, 0, 0, 0.12)',
  dividerStrong: 'rgba(0, 0, 0, 0.14)',
  surfaceTintSubtle: 'rgba(0, 0, 0, 0.03)',
  // Light neutrals between the paper and the ink: muted captions/metadata and
  // hairline rules in dense headers (live event dialog, hover cards).
  textMuted: '#9aa3b5',
  ruleSubtle: '#e4e8f4',
  // Fallback for blueprints whose tag group doesn't resolve (orphan data); tinted toward
  // the brand neutral. Mirrors $tag-group-fallback-color in _variables.scss.
  tagGroupFallback: '#9ea3b3',
  // Categorical series palette for multi-line / multi-segment charts.
  // Mirrors $segment-color-1..6 in _variables.scss; keep the two in sync.
  segment: ['#4eacc4', '#cc8145', '#b055c7', '#b53c3c', '#55c770', '#b9bf1f'],
  // Line-chart series palette: the six segment hues plus four extra so charts
  // plotting up to ten series never repeat a color. Deliberately independent of
  // any domain color (event-category tag colors, subgenre colors) — series
  // identity here means "the nth line in this chart", nothing more.
  // Mirrors $chart-series-color-1..10 in _variables.scss; keep the two in sync.
  chartSeries: ['#4eacc4', '#cc8145', '#b055c7', '#b53c3c', '#55c770', '#b9bf1f', '#3f6fd1', '#d1478f', '#1f8a70', '#8a4b2f'],
}

export const theme = createTheme({
  typography: {
    fontFamily: ['Walsheim-Regular', 'sans-serif'].join(','),
    h1: {
      fontSize: '1.5rem',
    },
    h2: {
      fontSize: '1.375rem',
    },
    h3: {
      fontSize: '1.1rem',
      fontWeight: 700,
    },
    h4: {
      fontSize: '0.8125rem',
    },
    h5: {
      fontSize: '0.78125rem',
    },
  },
  palette: {
    primary: {
      main: '#bd65cf',
      contrastText: '#fff',
    },
    secondary: {
      main: '#273143',
    },
    warning: {
      main: '#b13d37',
    },
    success: {
      main: '#38a73e',
    },
    text: {
      primary: '#273143',
    },
    background: {
      default: '#eaeefd',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 'bold',
          borderRadius: borderRadiusDefault,
        },
        contained: {
          color: 'white',
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: borderRadiusDefault,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          display: 'inline-block',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        underlineNone: {
          color: '#bd65cf',
          cursor: 'pointer',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadiusDefault,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          // override default title (h5) style only but allow altering through typography props
          '& span.MuiTypography-h5': {
            fontSize: 18,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          marginRight: '5px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          backgroundColor: '#f2f6ff',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          margin: '0px 8px 8px 0px',
          fontWeight: 'bold',
          height: 'auto',
        },
        sizeSmall: {
          height: 'auto',
        },
        labelSmall: {
          fontSize: '11px',
          padding: '3px 8px !important',
        },
        label: {
          fontSize: '12px',
          padding: '5px 10px !important',
        },
        colorPrimary: {
          backgroundColor: '#bd65cf',
          color: '#FFF',
        },
        colorSecondary: {
          backgroundColor: '#38a73e',
          color: '#FFF',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: '#273143',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        container: {
          minWidth: '0',
        },
        item: {
          flexShrink: 0,
        },
      },
      defaultProps: {
        wrap: 'nowrap',
        zeroMinWidth: true,
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          lineHeight: 'inherit',
          fontSize: '0.75rem',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginRight: '1.5rem',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: borderRadiusDefault,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: borderRadiusDefault,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: borderRadiusDefault,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: borderRadiusDefault,
        },
        standardInfo: {
          backgroundColor: '#e8eaf6', // Light purple background that complements #f2f6ff
          color: '#6a1b9a', // Darker purple for better text contrast
          '& .MuiAlert-icon': {
            color: '#6a1b9a', // Darker purple icon for better contrast
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        spacing: {
          margin: 8,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        spacing: {
          margin: 8,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            lineHeight: '1em',
          },
          '& .MuiInputLabel-shrink': {},
          '& .MuiOutlinedInput-root': {
            borderRadius: 32,
          },
        },
        popper: {
          '& > div': {
            borderRadius: borderRadiusDefault,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          color: '#777',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: '0.5rem',
          borderRadius: '0.5rem',
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
        bar: {
          borderRadius: '0.5rem',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 32,
          backgroundColor: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadiusDefault,
        },
        root: {
          borderRadius: borderRadiusDefault,
        },
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          padding: '24px 0',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
          '&:before': { display: 'none' },
          '&:not(:first-of-type)': {
            borderTop: `1px solid ${colors.dividerStrong}`,
          },
          '&.MuiAccordion-rounded:first-of-type': {
            borderTopLeftRadius: borderRadiusDefault,
            borderTopRightRadius: borderRadiusDefault,
          },
          '&.MuiAccordion-rounded:last-of-type': {
            borderBottomLeftRadius: borderRadiusDefault,
            borderBottomRightRadius: borderRadiusDefault,
          },
        },
      },
    },
  },
})
